import { cn } from "@/utils/theme";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  inputVariants,
  ScrollArea,
} from "@elaniintc/goes-design-system";
import { DropdownListItem, type IDropdownListItem } from "./dropdown-list-item";
import { DropdownListSeparator } from "./dropdown-list-separator";
import type { ComponentProps } from "react";
import { useMemo, useRef, useState } from "react";

interface DropdownListGroup {
  groupId: string;
  groupLabels: string[];
  source?: string;
  items: IDropdownListItem[];
  icon?: ComponentProps<typeof Icon> & {
    variant?:
      | "info"
      | "warning"
      | "disabled"
      | "action"
      | "success"
      | "danger"
      | "neutral"
      | null
      | undefined;
  };
}

export type DropdownListOptions = IDropdownListItem[] | DropdownListGroup[];

/** Type guard: grouped options */
const isGroupedOptions = (
  options: DropdownListOptions,
): options is DropdownListGroup[] => {
  return (
    Array.isArray(options) && options.length > 0 && "groupId" in options[0]
  );
};

/**
 * Matcher para relacionar un grupo/item del menú principal con un grupo del submenú.
 * Por defecto:
 *  - Coincide por groupId
 *  - Si existen groupLabels en el submenú, valida que comiencen con los labels del grupo padre
 */
export type SubmenuMatcher = (
  parent: { group?: DropdownListGroup; item: IDropdownListItem },
  submenuGroup: DropdownListGroup,
) => boolean;

/**
 * Callback para generar dinámicamente el submenú a partir de la selección del menú principal.
 * Si se define, tiene prioridad sobre `submenuOptions`.
 */
export type GetSubmenu = (parent: {
  group?: DropdownListGroup;
  item: IDropdownListItem;
}) => DropdownListOptions | undefined;

interface DropdownListProps {
  options: DropdownListOptions;
  /** Opcional: fuente de datos para submenú (estática). */
  submenuOptions?: DropdownListOptions;
  /** Opcional: generador dinámico de submenú. */
  getSubmenu?: GetSubmenu;
  /** Opcional: estrategia para vincular opción padre con grupo(s) del submenú. */
  submenuMatcher?: SubmenuMatcher;

  group?:
    | "middle"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down"
    | "down-left"
    | "down-right";
  iconPosition?: "left" | "right";
  widthClassName?: string; // e.g. "w-full", "w-64"
  search?: {
    placeholder?: string;
    query: string;
    onSearch: (query: string) => void;
  };
  placeholder?: string;
  selectedValue?: IDropdownListItem;
  selectedGroup?: DropdownListGroup;
  toggleSelection?: (
    value: IDropdownListItem,
    group?: DropdownListGroup,
  ) => void;
}

const DropdownListSubMenu = ({
  options,
  submenuOptions,
  getSubmenu,
  submenuMatcher,
  iconPosition = "left",
  search,
  widthClassName,
  placeholder = "Selecciona…",
  toggleSelection,
  selectedValue: selectedValueProp,
  selectedGroup: selectedGroupProp,
  group,
}: DropdownListProps) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"root" | "submenu">("root");

  const [internalSelectedItem, setInternalSelectedItem] = useState<
    IDropdownListItem | undefined
  >(undefined);
  const [internalSelectedGroup, setInternalSelectedGroup] = useState<
    DropdownListGroup | undefined
  >(undefined);

  const selectedItem = selectedValueProp ?? internalSelectedItem;
  const selectedGroup = selectedGroupProp ?? internalSelectedGroup;

  const lastParentGroupRef = useRef<DropdownListGroup | undefined>(undefined);
  const lastParentItemRef = useRef<IDropdownListItem | undefined>(undefined);

  const [parentGroup, setParentGroup] = useState<DropdownListGroup | undefined>(
    undefined,
  );
  const [parentItem, setParentItem] = useState<IDropdownListItem | undefined>(
    undefined,
  );

  const defaultMatcher: SubmenuMatcher = (parent, submenuGroup) => {
    if (parent.group && submenuGroup.groupId !== parent.group.groupId)
      return false;
    if (parent.group?.groupLabels?.length) {
      const base = parent.group.groupLabels.join("/");
      const target = submenuGroup.groupLabels
        ?.slice(0, parent.group.groupLabels.length)
        .join("/");
      if (base !== target) return false;
    }
    return true;
  };

  const effectiveMatcher = submenuMatcher ?? defaultMatcher;

  const getComputedSubmenu = (
    ctxItem?: IDropdownListItem,
    ctxGroup?: DropdownListGroup,
  ): DropdownListOptions | undefined => {
    if (!ctxItem) return undefined;

    const source = getSubmenu
      ? getSubmenu({ group: ctxGroup, item: ctxItem })
      : submenuOptions;
    if (!source) return undefined;

    if (!isGroupedOptions(source)) return source;
    return source.filter((g) =>
      effectiveMatcher({ group: ctxGroup, item: ctxItem }, g),
    );
  };

  const computedSubmenu = useMemo<DropdownListOptions | undefined>(() => {
    return getComputedSubmenu(parentItem, parentGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentGroup, parentItem, submenuOptions, getSubmenu, effectiveMatcher]);

  const selectAndClose = (
    item: IDropdownListItem,
    group?: DropdownListGroup,
  ) => {
    setInternalSelectedItem(item);
    setInternalSelectedGroup(group);

    if (toggleSelection) toggleSelection(item, group);

    lastParentGroupRef.current = parentGroup ?? group;
    lastParentItemRef.current = parentItem ?? item;

    setOpen(false);
  };

  const handleRootItemClick = (
    item: IDropdownListItem,
    group?: DropdownListGroup,
  ) => {
    setParentGroup(group);
    setParentItem(item);

    const next = getSubmenu ? getSubmenu({ group, item }) : submenuOptions;
    const hasSubmenu = !!(
      next && (Array.isArray(next) ? next.length > 0 : true)
    );

    if (hasSubmenu) {
      setView("submenu");
      setOpen(true);

      lastParentGroupRef.current = group;
      lastParentItemRef.current = item;
    } else {
      selectAndClose(item, group);
    }
  };

  const handleSubmenuItemClick = (
    item: IDropdownListItem,
    group?: DropdownListGroup,
  ) => {
    selectAndClose(item, group);
  };

  const goBackToRoot = () => {
    setView("root");
    setOpen(true);
  };

  const displayLabel = ((): string => {
    const lbl = selectedItem?.label;
    if (lbl === undefined || lbl === null || String(lbl).trim() === "")
      return String(placeholder);
    return String(lbl);
  })();

  const TriggerButton = (
    <Button
      group={group}
      variant="tertiary"
      className={cn(
        "flex justify-start items-center group min-w-0 relative gap-2",
        inputVariants({
          className: cn(
            "bg-neutral-object hover:bg-neutral-object-accent-silent data-[state=open]:bg-neutral-object-accent-quiet data-[state=open]:border-neutral-loud",
            widthClassName,
          ),
          group,
        }),
      )}
      aria-label={displayLabel}
    >
      {selectedGroup?.icon && (
        <Badge
          shape="pill"
          size="sm"
          variant={selectedGroup.icon.variant || "action"}
          className={cn(
            "min-w-[24px] px-1 flex justify-center items-center text-body",
          )}
        >
          <Icon {...selectedGroup.icon} />
        </Badge>
      )}

      {selectedItem?.icon && (
        <Icon {...selectedItem.icon} className="text-body" />
      )}
      <p
        className={cn(
          "truncate font-normal pr-5",
          selectedItem?.label ? "text-body" : "text-body-quiet",
        )}
      >
        {displayLabel}
      </p>

      <Icon
        type="far"
        name="angle-down"
        className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
      />
    </Button>
  );

  const SearchBox = search ? (
    <div className="border-b border-neutral-quiet p-3 relative ">
      <Icon
        type="far"
        name="search"
        className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
      />
      <Input
        variant="sm"
        className="text-sm w-full font-normal flex-auto pl-9"
        placeholder={search.placeholder || "Buscar..."}
        onChange={(e) => search.onSearch(e.target.value)}
        value={search.query}
      />
    </div>
  ) : null;

  const RootView = (
    <ScrollArea className="h-[264px] p-2">
      {isGroupedOptions(options) ? (
        (options as DropdownListGroup[]).map((g) => (
          <div key={g.groupId} className="flex flex-col gap-2 mb-2">
            {/* En root, los group labels actúan como separadores informativos */}
            <DropdownListSeparator options={g.groupLabels} icon={g.icon} />
            {g.items.map((item) => (
              <DropdownListItem
                key={item.value}
                iconPosition={iconPosition}
                {...item}
                toggleSelection={(i) => handleRootItemClick(i, g)}
              />
            ))}
          </div>
        ))
      ) : (options as IDropdownListItem[]).length > 0 ? (
        (options as IDropdownListItem[]).map((item) => (
          <DropdownListItem
            key={item.value}
            iconPosition={iconPosition}
            {...item}
            toggleSelection={(i) => handleRootItemClick(i)}
          />
        ))
      ) : (
        <div className="flex items-center justify-center py-5">
          <span className="text-body-quiet">No se encontraron resultados</span>
        </div>
      )}
    </ScrollArea>
  );

  const SubmenuView = (
    <div className="flex flex-col max-h-[300px]">
      <ScrollArea className="h-[240px] p-2">
        {computedSubmenu && isGroupedOptions(computedSubmenu) ? (
          (computedSubmenu as DropdownListGroup[]).map((g) => (
            <div key={g.groupId} className="flex flex-col gap-2 mb-2">
              <div onClick={goBackToRoot}>
                <DropdownListSeparator
                  className="cursor-pointer"
                  options={g.groupLabels}
                  icon={g.icon}
                  onClick={goBackToRoot}
                />
              </div>
              {g.items.map((item) => (
                <DropdownListItem
                  key={item.value}
                  iconPosition={iconPosition}
                  {...item}
                  toggleSelection={(i) => handleSubmenuItemClick(i, g)}
                />
              ))}
            </div>
          ))
        ) : computedSubmenu &&
          Array.isArray(computedSubmenu) &&
          computedSubmenu.length > 0 ? (
          (computedSubmenu as IDropdownListItem[]).map((item) => (
            <DropdownListItem
              key={item.value}
              iconPosition={iconPosition}
              {...item}
              toggleSelection={(i) => handleSubmenuItemClick(i)}
            />
          ))
        ) : (
          // Mostrar separador aunque no existan opciones
          <div className="flex flex-col gap-2 mb-2">
            <div onClick={goBackToRoot}>
              <DropdownListSeparator
                className="cursor-pointer"
                options={[
                  ...(parentGroup?.groupLabels ?? []),
                  ...(parentItem?.label ? [String(parentItem.label)] : []),
                ]}
                icon={parentGroup?.icon}
                onClick={goBackToRoot}
              />
            </div>
            <div className="flex items-center justify-center py-5">
              <span className="text-body-quiet">Sin opciones disponibles</span>
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(v) => {
        if (v) {
          const ctxItem = lastParentItemRef.current ?? selectedItem;
          const ctxGroup = lastParentGroupRef.current ?? selectedGroup;
          const hasCtxSubmenu = !!getComputedSubmenu(ctxItem, ctxGroup);
          if (hasCtxSubmenu && ctxItem) {
            setParentItem(ctxItem);
            setParentGroup(ctxGroup);
            setView("submenu");
          } else {
            setView("root");
          }
        }
        setOpen(v);
      }}
    >
      <DropdownMenuTrigger asChild>{TriggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl w-[var(--radix-dropdown-menu-trigger-width)]",
          widthClassName,
        )}
        side="bottom"
        align="start"
      >
        {SearchBox}
        {view === "root" ? RootView : SubmenuView}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { DropdownListSubMenu };
